const BASE = '/s/collection/signs?pub_sign_type=has_any%3A';

const SIGNS_CATEGORIES = [
  {
    key: 'welcome',
    href: `${BASE}home-blessings`,
    title: 'Welcome & Home Blessings',
  },
  {
    key: 'brachos',
    href: `${BASE}brachos-tefillos`,
    title: 'Daily Brachos & Tefillos',
  },
  {
    key: 'shabbos',
    href: `${BASE}shabbos`,
    title: 'Shabbos & Candle Lighting',
  },
  {
    key: 'israel',
    href: `${BASE}israel-jerusalem`,
    title: 'Israel & Jerusalem',
  },
  {
    key: 'gedolim',
    href: `${BASE}gedolim-rebbe-quotes`,
    title: 'Gedolim & Rebbe Quotes',
  },
  {
    key: 'pesukim',
    href: `${BASE}pesukim-text-art`,
    title: 'Pesukim & Text Art',
  },
  {
    key: 'wedding',
    href: `${BASE}wedding-family-personalized`,
    title: 'Wedding, Family & Personalized',
  },
];

const getSchema = rootURL => {
  const signsURL = `${rootURL}/s/collection/signs`;
  return [
    {
      '@type': 'CollectionPage',
      '@id': `${signsURL}#collectionpage`,
      url: signsURL,
      name: 'Signs & Plaques for the Jewish Home',
      description:
        'Shop Jewish signs and plaques, including Asher Yatzar, Birkat Habayit, Bruchim Habaim, Hadlakat Neirot, Eshet Chayil, Rebbe quotes, pesukim, Israel-themed text art, and meaningful plaques by independent artists.',
      isPartOf: {
        '@type': 'WebSite',
        '@id': `${rootURL}/#website`,
        name: 'Joodaic',
        url: rootURL,
      },
      about: [
        { '@type': 'Thing', name: 'Jewish signs' },
        { '@type': 'Thing', name: 'Jewish plaques' },
        { '@type': 'Thing', name: 'Asher Yatzar signs' },
        { '@type': 'Thing', name: 'Birkat Habayit' },
        { '@type': 'Thing', name: 'Bruchim Habaim' },
        { '@type': 'Thing', name: 'Hadlakat Neirot' },
        { '@type': 'Thing', name: 'Eshet Chayil' },
        { '@type': 'Thing', name: 'Rebbe quotes' },
        { '@type': 'Thing', name: 'Pesukim' },
        { '@type': 'Thing', name: 'Israel-themed text art' },
        { '@type': 'Thing', name: 'Jewish text art' },
        { '@type': 'Thing', name: 'Hebrew wall art' },
      ],
      mainEntity: {
        '@type': 'ItemList',
        name: 'Jewish signs and plaques collection',
        itemListOrder: 'https://schema.org/ItemListUnordered',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Welcome & Home Blessings',
            description: 'Birkat Habayit, Bruchim Habaim, and home blessing plaques.',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Daily Brachos & Tefillos',
            description:
              'Asher Yatzar, Al Hamichya, Modeh Ani, Birkat Haesek, and other daily bracha plaques.',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'Shabbos & Candle Lighting',
            description:
              'Hadlakat Neirot, Shabbos Licht, Eshet Chayil, and Shabbos-related text pieces.',
          },
          {
            '@type': 'ListItem',
            position: 4,
            name: 'Israel & Jerusalem',
            description:
              'Kotel, Jerusalem, Im Eshkachech, Am Yisrael Chai, and Israel-themed text art.',
          },
          {
            '@type': 'ListItem',
            position: 5,
            name: 'Gedolim & Rebbe Quotes',
            description:
              'Portrait-led pieces of gedolim and Rebbes with quotes, pesukim, or teachings.',
          },
          {
            '@type': 'ListItem',
            position: 6,
            name: 'Pesukim & Text Art',
            description:
              'Ani Maamin, Shema Yisrael, Tehillim, Chai, Ahava, and Jewish calligraphy pieces.',
          },
          {
            '@type': 'ListItem',
            position: 7,
            name: 'Wedding, Family & Personalized',
            description:
              "Ani L'dodi V'dodi Li, chuppah glass wall art, family plaques, and personalized Jewish pieces.",
          },
        ],
      },
    },
    {
      '@type': 'FAQPage',
      '@id': `${signsURL}#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What are signs and plaques for a Jewish home?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Signs and plaques for a Jewish home include wall pieces built around Jewish words, brachos, pesukim, tefillos, quotes, or phrases. Common examples include Asher Yatzar signs, Birkat Habayit plaques, Bruchim Habaim entrance pieces, Hadlakat Neirot, Eshet Chayil, Birkat Haesek, Rebbe quotes, and Jewish text art.',
          },
        },
        {
          '@type': 'Question',
          name: 'Where should an Asher Yatzar sign be placed?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'An Asher Yatzar sign is usually placed near a bathroom, washing area, or another practical spot where it will be seen after use. Some options are wall-mounted, while others can be placed on a shelf, counter, or stand, depending on the design.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is a good Jewish sign for an entrance?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'For an entrance, common choices include Bruchim Habaim, Birkat Habayit, Am Yisrael Chai, or a welcoming Hebrew plaque. If you are looking for a custom family name or door sign, browse personalized Jewish nameplates.',
          },
        },
        {
          '@type': 'Question',
          name: 'Are these pieces considered art or Judaica?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Some pieces are closer to Judaica, especially practical plaques like Asher Yatzar, Al Hamichya, or Birkat Haesek. Others are closer to art, such as watercolor Mizmor Letodah, Jerusalem scenes with pesukim, Rebbe portraits with quotes, or modern calligraphy pieces. This collection brings them together because they are all text-led Jewish wall pieces.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the difference between Jewish art, Judaica, and signs or plaques?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Jewish art is often chosen mainly for the image, style, or subject. Judaica is usually connected to Jewish practice, ritual, or home life. Signs and plaques sit between the two: they are decorative, but they are built around Jewish text, such as a bracha, pasuk, tefillah, quote, or phrase.',
          },
        },
        {
          '@type': 'Question',
          name: 'What Jewish signs or plaques make good gifts?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "For a new home, Birkat Habayit and Bruchim Habaim are natural choices. For a wedding or couple, Ani L'dodi V'dodi Li, chuppah glass art, Eshet Chayil, or a personalized plaque can work well. For a business or office, Birkat Haesek is the clearest fit. Pieces with a clear place or use are usually easier to gift than general artwork.",
          },
        },
      ],
    },
  ];
};

const signsSeoConfig = {
  key: 'signs',
  match: ({ collectionSlug }) => collectionSlug === 'signs',
  accentColor: '--colorJudaica',
  links: SIGNS_CATEGORIES,
  title: 'Jewish Signs & Plaques | Asher Yatzar & Birkat Habayit | Joodaic',
  description:
    'Shop Jewish signs and plaques, including Asher Yatzar, Birkat Habayit, Bruchim Habaim, Hadlakat Neirot, Eshet Chayil, Rebbe quotes, pesukim, Israel-themed text art, and meaningful plaques by independent artists.',
  getSchema,
  intro: {
    heading: 'Signs & Plaques for the Jewish Home',
    tagline:
      'Jewish signs, plaques, brachos, pesukim, Rebbe quotes, and text-led wall art for the home, office, Shabbos table, and meaningful gifts.',
    collapsedHeading: 'Jewish Signs & Plaques',
    bodyParagraphs: [
      'Some Jewish wall pieces are chosen for the image. Others are chosen for the words.<br />This collection brings both together: Asher Yatzar signs, Birkat Habayit plaques, Bruchim Habaim pieces, Hadlakat Neirot, Eshet Chayil, pesukim, Rebbe quotes, Israel-themed text art, and meaningful plaques for Jewish homes, offices, and gifts. The style may be wood, acrylic, watercolor, lucite, mixed media, or framed art. The common thread is the text.',
    ],
    linksLabel: 'Browse by Category',
    linksNote:
      'Looking for custom family names or door plaques? Browse our <a href="/s/judaica/nameplates">personalized Jewish nameplates</a>.',
  },
  bottomSections: [
    {
      id: 'welcome_home',
      title: 'Welcome & Home Blessings',
      paragraphs: [
        'Best for entrances, hallways, and main living spaces: Birkat Habayit, Bruchim Habaim, Home Blessing, Am Yisrael Chai, and welcoming Jewish plaques.',
        'These pieces are often chosen for the front of the home, the entryway, or the rooms people notice first.',
      ],
    },
    {
      id: 'brachos_tefillos',
      title: 'Daily Brachos & Tefillos',
      paragraphs: [
        'Best for daily-use areas: Asher Yatzar near a bathroom or washing area, Al Hamichya near a kitchen, Modeh Ani, Birkat Kohanim, Mi Sheberach, and Birkat Haesek for an office or business.',
        'These are pieces with a practical rhythm. They belong where the bracha or tefillah is actually used.',
      ],
    },
    {
      id: 'shabbos',
      title: 'Shabbos & Candle Lighting',
      paragraphs: [
        'Best for the dining room, candle-lighting area, or Shabbos table: Hadlakat Neirot, Tefilat Hadlakat Neirot, Shabbos Licht, Eshet Chayil, and Shabbos-related text pieces.',
      ],
    },
    {
      id: 'israel_jerusalem',
      title: 'Israel & Jerusalem',
      paragraphs: [
        'Best for homes that want a visible connection to Yerushalayim, Eretz Yisrael, or the Jewish people: Kotel scenes, Jerusalem pieces, Im Eshkachech, Am Yisrael Chai, Kever Rachel, soldier/prayer pieces, and Israel-themed text art.',
        'This category covers both artwork and text-led pieces where Israel or Jerusalem is the central theme.',
      ],
      paragraphsAfter: [
        'For Jerusalem and Kotel paintings without text, browse <a href="/s/art/the_classics">Jerusalem &amp; Kotel art</a>.',
      ],
    },
    {
      id: 'gedolim_quotes',
      title: 'Gedolim & Rebbe Quotes',
      paragraphs: [
        'Best for portrait-led pieces with words: Rav Chaim, Rav Ovadia, Baba Sali, The Rebbe, Reb Shayele, Chafetz Chaim, and other gedolim or Rebbes paired with a quote, pasuk, or teaching.',
        'If the face leads the piece, it belongs here.',
      ],
    },
    {
      id: 'pesukim_text',
      title: 'Pesukim & Text Art',
      paragraphs: [
        'Best for Jewish words as art: Ani Maamin, Shema Yisrael, Shirat HaYam, Tehillim, Breslov phrases, Chai, Ahava, modern calligraphy, and other text-led pieces.',
        'If it is mainly a phrase, pasuk, or calligraphy piece, it belongs here.',
      ],
    },
    {
      id: 'wedding_family',
      title: 'Wedding, Family & Personalized',
      paragraphs: [
        "Best for couples, kallahs, families, and new homes: Ani L'dodi V'dodi Li, chuppah glass wall art, family plaques, personalized Birkat Habayit, and meaningful pieces with a gift or family connection.",
        'These pieces work well as gifts because the occasion is built into the piece itself.',
      ],
    },
    {
      id: 'room_or_use',
      title: 'Choosing by Room or Use',
      paragraphs: [
        'For an entrance, buyers often look for Bruchim Habaim, Birkat Habayit, Am Yisrael Chai, or another piece that feels welcoming without being too loud.',
        'For a bathroom or washing area, Asher Yatzar is the natural choice. Acrylic, lucite, wood, and framed options each give a different look, depending on whether you want something subtle, modern, or decorative.',
        'For a kitchen, dining room, or Shabbos area, Hadlakat Neirot, Eshet Chayil, Mizmor Letodah, Al Hamichya, and similar pieces often feel more at home than generic decor. They belong to the rhythm of the room.',
        'For an office or business, Birkat Haesek is a clear and practical choice. It has a defined place, a defined meaning, and does not depend as much on personal art taste.',
      ],
    },
    {
      id: 'hebrew_english',
      title: 'Hebrew, English, or Transliterated Text?',
      paragraphs: [
        'There is no single right format. Some homes prefer Hebrew text, especially for familiar brachos, pesukim, and tefillos. Others prefer English translation, transliteration, or a mix, especially for gifts or homes where not everyone reads Hebrew easily.',
        'The better choice is usually the one people will actually connect with when they see it every day.',
      ],
    },
    {
      id: 'materials_styles',
      title: 'Materials and Styles',
      paragraphs: [
        'Signs and plaques can feel very different depending on the material. Wood feels warm and handmade. Acrylic and lucite feel cleaner and more modern. Stone has a heavier, more classic presence. Watercolor and framed paper pieces feel closer to art. Mixed media can sit somewhere in between, combining image, texture, and words.',
        'That range is the point. A Birkat Habayit for an entrance, an Asher Yatzar near a sink, a Rebbe portrait with a quote, and a Mizmor Letodah watercolor do not need to look like the same product type. They just need to make sense in the home.',
      ],
    },
    {
      id: 'gifts',
      title: 'Gifts for Weddings, New Homes, and Offices',
      paragraphs: [
        'Text-led Jewish pieces often make strong gifts because they have a clearer place than general artwork. A Birkat Habayit can belong near an entrance. Ani L\'dodi V\'dodi Li works naturally for a couple. Hadlakat Neirot or Eshet Chayil can feel right near the Shabbos table. Birkat Haesek suits a new office, shop, or business.',
        'For gifts, the safest choices are usually pieces with a clear use, location, or occasion. A sign that belongs at the entrance, near candle lighting, in the kitchen, or in an office is easier to give than art that depends completely on personal taste.',
      ],
      paragraphsAfter: [
        'For family names, children\'s rooms, shtender plaques, or custom door signs, browse our <a href="/s/judaica/nameplates">personalized Jewish nameplates</a>.',
        'If you are choosing something for the doorway, you may also want to browse <a href="/s/judaica/mezuzas">mezuzah cases for the entrance</a>.',
        'For other <a href="/s/judaica">handmade Judaica for the home</a>, browse the full Judaica collection.',
        'For couples, kallahs, and housewarmings, browse <a href="/s/collection/wedding-new-home-gifts">Jewish wedding and new home gifts</a>.',
      ],
    },
  ],
  faqHeading: 'FAQ',
  faq: [
    {
      question: 'What are signs and plaques for a Jewish home?',
      answer: [
        'Signs and plaques for a Jewish home include wall pieces built around Jewish words, brachos, pesukim, tefillos, quotes, or phrases. Common examples include Asher Yatzar signs, Birkat Habayit plaques, Bruchim Habaim entrance pieces, Hadlakat Neirot, Eshet Chayil, Birkat Haesek, Rebbe quotes, and Jewish text art.',
      ],
    },
    {
      question: 'Where should an Asher Yatzar sign be placed?',
      answer: [
        'An Asher Yatzar sign is usually placed near a bathroom, washing area, or another practical spot where it will be seen after use. Some options are wall-mounted, while others can be placed on a shelf, counter, or stand, depending on the design.',
      ],
    },
    {
      question: 'What is a good Jewish sign for an entrance?',
      answer: [
        'For an entrance, common choices include Bruchim Habaim, Birkat Habayit, Am Yisrael Chai, or a welcoming Hebrew plaque. If you are looking for a custom family name or door sign, browse personalized Jewish nameplates.',
      ],
    },
    {
      question: 'Are these pieces considered art or Judaica?',
      answer: [
        'Some pieces are closer to Judaica, especially practical plaques like Asher Yatzar, Al Hamichya, or Birkat Haesek. Others are closer to art, such as watercolor Mizmor Letodah, Jerusalem scenes with pesukim, Rebbe portraits with quotes, or modern calligraphy pieces. This collection brings them together because they are all text-led Jewish wall pieces.',
      ],
    },
    {
      question: 'What is the difference between Jewish art, Judaica, and signs or plaques?',
      answer: [
        'Jewish art is often chosen mainly for the image, style, or subject. Judaica is usually connected to Jewish practice, ritual, or home life. Signs and plaques sit between the two: they are decorative, but they are built around Jewish text, such as a bracha, pasuk, tefillah, quote, or phrase.',
      ],
    },
    {
      question: 'What Jewish signs or plaques make good gifts?',
      answer: [
        "For a new home, Birkat Habayit and Bruchim Habaim are natural choices. For a wedding or couple, Ani L'dodi V'dodi Li, chuppah glass art, Eshet Chayil, or a personalized plaque can work well. For a business or office, Birkat Haesek is the clearest fit.",
        'Pieces with a clear place or use are usually easier to gift than general artwork.',
      ],
    },
  ],
};

export default signsSeoConfig;
